import { DomesticConstants } from "@assets/constants";
import { DomesticFormTypes } from "@assets/formTypes";
import { DomesticHelper } from "@assets/helpers";
import { CommonTypes, DomesticTypes } from "@assets/types";
import { dateToUTC_WithoutTimezoneChange } from "@assets/utilities/dateUtilities";
import UserService from "@services/UserService";

/**
 * Convert a Domestic form to a Domestic Exposed Party
 */
const domesticFormToParty = (
  formData: DomesticFormTypes.CreateForm,
  fieldNames: DomesticFormTypes.BookingPartySectionFields,
  term: CommonTypes.Nullable<DomesticConstants.PAYER_TERM_TYPE>
) => {
  const isCompany =
    formData[fieldNames.BookingPartyType] ===
    DomesticConstants.BOOKING_PARTY_TYPE.COMPANY;

  const isUserAgentOrEmployee =
    UserService.isUserAgent() || UserService.isUserEmployee();

  const isNewAddress = formData[fieldNames.IsNewAddress];
  const isCustomerFromNatReg =
    formData[fieldNames.Customer]?.Source ===
    DomesticConstants.CUSTOMER_SOURCE.NATREG;
  const isPersonInputFillable =
    isNewAddress ||
    isCustomerFromNatReg ||
    (!isUserAgentOrEmployee && !isCompany);

  const address: DomesticTypes.DomesticExposedAddress | null = {
    Street: formData[fieldNames.Address],
    PostalCode: formData[fieldNames.PostalCode].PostCode ?? "",
    CountryISOCode: DomesticConstants.ICELAND_ISO_CODE,
    City: formData[fieldNames.PostalCode].City ?? "",
  };
  const contact: DomesticTypes.DomesticExposedContact | null =
    isPersonInputFillable
      ? {
          PhoneNumber: formData[fieldNames.Phone],
          Email: formData[fieldNames.Email],
        }
      : null;

  const party: DomesticTypes.DomesticExposedParty = {
    PartyIdentification: formData[fieldNames.SSN],
    PartyType: formData[fieldNames.BookingPartyType],
    Name: formData[fieldNames.Name],
    AddressNumber:
      isCompany && formData[fieldNames.Department]
        ? formData[fieldNames.Department].AddressNo
        : !isPersonInputFillable
        ? formData[fieldNames.SSN]
        : null,
    Address: address,
    Contact: contact,
    Terms: term,
  };

  return party;
};

/**
 * Convert a Domestic form to a Domestic Exposed Delivery Method
 */
const domesticFormToDeliveryMethod = (
  formData: DomesticFormTypes.CreateForm,
  fieldNames: DomesticFormTypes.PlacesSectionFields
) => {
  const isAddressDelivery =
    formData[fieldNames.Method] === DomesticConstants.DELIVERY_MODE.ADDRESS;

  const deliveryMethod: DomesticTypes.DomesticExposedDeliveryMethod = {
    DeliveryType: formData[fieldNames.Method],
    RequestDate:
      isAddressDelivery && formData[fieldNames.Date]
        ? dateToUTC_WithoutTimezoneChange(formData[fieldNames.Date])
        : null,
    Address: null,
    LocationContactName: isAddressDelivery
      ? formData[fieldNames.ContactName]
      : null,
    LocationContactPhone: isAddressDelivery
      ? formData[fieldNames.ContactPhone]
      : null,
  };

  return deliveryMethod;
};

/**
 * Convert a Domestic form cargo detail to a Domestic Exposed cargo detail
 */
const domesticCargoDetailFormToDomesticCargoDetail = (
  cargoDetail: DomesticFormTypes.CargoDetailForm
) => {
  const exposedCargoDetail: DomesticTypes.DomesticExposedCargoDetail = {
    Description: cargoDetail.Description,
    UNNumber:
      cargoDetail.Hazardous && cargoDetail.HazardousMaterial
        ? cargoDetail.HazardousMaterial.Code
        : null,
    ItemCondition: cargoDetail.Type?.Code ?? null,
    IsFragile: cargoDetail.Fragile,
    Quantity: cargoDetail.Quantity ?? 0,
    TotalWeight: cargoDetail.Weight ?? 0,
  };
  return exposedCargoDetail;
};

/**
 * Convert a Domestic form to a Domestic Exposed Booking
 */
export const domesticFormToCreateUpdateDomesticBooking = (
  formData: DomesticFormTypes.CreateForm
) => {
  const { senderTerms, receiverTerms } = DomesticHelper.getSenderReceiverTerms(
    formData.PickupMethod,
    formData.DropOffMethod,
    formData.IsSeparatePayerLegs,
    formData.Payer
  );

  const sender: DomesticTypes.DomesticExposedParty = domesticFormToParty(
    formData,
    DomesticFormTypes.bookingPartyFieldNames.SENDER,
    senderTerms
  );

  const receiver: DomesticTypes.DomesticExposedParty = domesticFormToParty(
    formData,
    DomesticFormTypes.bookingPartyFieldNames.RECEIVER,
    receiverTerms
  );

  const pickup = domesticFormToDeliveryMethod(
    formData,
    DomesticFormTypes.placeFieldNames.PLR
  );

  const dropOff = domesticFormToDeliveryMethod(
    formData,
    DomesticFormTypes.placeFieldNames.PFD
  );

  const domesticBooking: DomesticTypes.DomesticExposedBooking = {
    Sender: sender,
    Receiver: receiver,
    BookingParty: formData.BookingParty,
    PayingParty: DomesticHelper.getPayer(
      formData.Payer,
      formData.PickupMethod,
      formData.DropOffMethod
    ),
    PickUp: pickup,
    DropOff: dropOff,
    OrderLines: formData.CargoDetails.map((cargoDetail) =>
      domesticCargoDetailFormToDomesticCargoDetail(cargoDetail)
    ),
    Comment: formData.Comment,
    IsChristmasPackage: formData.IsChristmasPackage,
  };

  return domesticBooking;
};
